// OnshapeCompsBlock — sync trigger + the resulting component list for an
// Onshape surface (UI_VISION §8 step 2: "Sync — trigger, and see the resulting
// component list"). The Onshape side panel is NOT a lesser surface (Alex: "I
// see what the other is seeing when I have an Onshape, and I can do a better
// job") — so it shows the SAME components grid as the Product screen, via the
// same shared pieces:
//   • OnshapeSyncReview — the ONE-BUTTON sync (ON-21 step 4, 2026-08-21). The
//     panel used to carry the old Preview→Apply flow while the Product screen
//     had the real sync — and Alex clicked Prepare here, saw a fresh BOM, and
//     nothing synced (the TTC test). One surface, one behaviour: the same
//     component the Product screen uses, auto-apply and all.
//   • SyncWorklistSection — Not synced, with the per-part Onshape links; in the
//     panel this is where an unregistered part names itself while the model is
//     open right beside it, which is the best possible place to fix it.
//   • makeGridCols + DataGrid — the components grid columns, read-only here
//     (no onSave/onAdd); full editing stays on the Product screen.
//   • resolveComps — the client resolver at typical values ({} overrides).
//
// Pure DB reads otherwise: the product fetch is /api/products/:id, and the
// grid re-fetches after an Apply so the list shows what the sync produced.

import { useState, useEffect, useMemo, useCallback } from 'react';
import DataGrid from './DataGrid.jsx';
import SyncWorklistSection from './SyncWorklistSection.jsx';
import SyncNotApplicableSection from './SyncNotApplicableSection.jsx';
import { makeGridCols, componentsCountLabel } from '../product-cols.jsx';
import { resolveComps } from '../product-resolver.js';
import { WARN } from '../icons.js';
import Card from './Card.jsx';
import { CARD_STYLE } from '../theme-tokens.js';

// ⚠ THE SYNC TRIGGER IS NOT IN THIS BLOCK ANY MORE (2026-08-21). On the Product
// screen OnshapeSyncReview sits in the 3D MODEL card, not with the components,
// and Alex's rule for these two surfaces is "same must be the same" - so the
// panel now puts it there too and this block renders only what its name says.
// The token that ties the three cards together is therefore owned ONE level up,
// exactly as ProductsView owns `worklistToken` for the same three sections.
export default function OnshapeCompsBlock({
  productId, refreshToken = 0, notSyncedCount = 0, storageKeyPrefix = 'onshape-panel:section',
}) {
  const [product, setProduct] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(() => {
    if (!productId) return;
    fetch(`/api/products/${productId}`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(p => { setProduct(p); setError(null); })
      .catch(err => setError(err.message));
  }, [productId]);

  useEffect(() => {
    setProduct(null);
    setError(null);
    load();
  }, [load]);

  // ON-15 — the panel's Refresh re-reads the DATABASE, never Onshape (Alex,
  // 2026-08-16: "to avoid refreshing completely Onshape"). Both halves move
  // together: the product fetch AND the worklist's own token, so the grid and
  // Not synced always show the same moment rather than two different ones.
  //
  // No `setProduct(null)` here, unlike the mount effect above: a refresh replaces
  // what is on screen, it does not restart the block, and blanking to "Loading
  // components…" for a DB read that takes milliseconds is a flash that says
  // something was lost. `refreshToken` starts at 0, which is the mount the effect
  // above already served — so 0 means "nothing to do", not "refresh once more".
  useEffect(() => {
    if (!refreshToken) return;
    load();
  }, [refreshToken, load]);

  const rows = useMemo(() => {
    if (!product) return [];
    return resolveComps(
      product.comps, product.params, {},
      product._labor_rate ?? product.trade ?? null, product._labor_productivity,
    ).map((r, i) => ({ ...r, _seq: i + 1 }));
  }, [product]);

  // No navigation callbacks — the ↗ ref-nav column renders empty, which is
  // right for a narrow panel that already IS somewhere else.
  const cols = useMemo(() => makeGridCols('definition', null, null), []);

  // TWO CARDS, NOT ONE (Alex, 2026-08-21: "The DB has two cards for Components &
  // Not Synced... The Onshape as one single card. Is there a reason? I would
  // prefer two.").
  //
  // There was no reason, and the comment that used to sit at the call site made
  // that worse by defending the fusion with a claim that was simply FALSE — it
  // said this matched "one Card holding a composite block... same as it does on
  // the Product screen". The Product screen does the opposite: `components` and
  // `sync-worklist` are two sibling spots in productSpots, and have been since the
  // card pass. So the panel was the odd one out and the note explained away the
  // drift instead of catching it.
  //
  // The split also earns its keep in a NARROW panel specifically: two cards
  // collapse independently, so the components grid can be folded away while the
  // worklist — the part you are actually working through while re-tagging — stays
  // open, which the fused card could never do.
  //
  // Not synced gets a card with NO TITLE, because SyncWorklistSection draws its
  // own heading and count; that is the same "card, no title" shape Spot uses for
  // this very section on the Product screen (HierarchyWorkspace.jsx), so the box
  // is added without doubling the title. `topRule={false}` for the same reason —
  // the card border already separates it, so its own top rule would be a second
  // divider in the same place.
  return (
    <>
      <Card
        storageKey={`${storageKeyPrefix}:components`}
        title="Components"
        count={product ? componentsCountLabel(rows.length, notSyncedCount) : undefined}
      >
        {error ? (
          <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-status-deprecated)' }}>{WARN} {error}</div>
        ) : !product ? (
          <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-hint)' }}>Loading components…</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <div style={{ fontSize: 'var(--fs-small)', color: 'var(--th-text-disabled)' }}>
              edit on the Product screen
            </div>
            <div style={{ overflowX: 'auto' }}>
              <DataGrid
                rows={rows}
                columns={cols}
                emptyMessage="No components yet — Sync from Onshape above, or add rows on the Product screen."
                storageKey="onshape-comps"
              />
            </div>
          </div>
        )}
      </Card>

      {/* Gated on the ref HERE, not left to SyncWorklistSection's own null return
          — Spot hit exactly this and drew "a loose rectangle" around nothing,
          because an unevaluated element is truthy no matter what it renders. */}
      {product && product.onshape_ref && (
        <div style={CARD_STYLE}>
          <SyncWorklistSection
            productId={product.id}
            onshapeRef={product.onshape_ref}
            refreshToken={refreshToken}
            onPromoted={load}
            topRule={false}
          />
        </div>
      )}

      {/* The third place a part can land (2026-10-11) — same card shape as Not
          synced, same gate. Components + Not synced + Not applicable = every part. */}
      {product && product.onshape_ref && (
        <div style={CARD_STYLE}>
          <SyncNotApplicableSection
            productId={product.id}
            onshapeRef={product.onshape_ref}
            refreshToken={refreshToken}
            topRule={false}
          />
        </div>
      )}
    </>
  );
}
